// ===================================================
// GAM Command Center — Supabase Client (Legacy Re-export)
// ===================================================
// All existing imports of `supabase` from this file now use
// the cookie-aware browser client from @supabase/ssr.
//
// LAZY (2026-08-27). This used to be `export const supabase = createClient()`,
// which built a client the moment ANY module in the graph was imported —
// including during static prerender, where no browser client is ever used.
// A missing NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY therefore killed `next build`
// at *require* time on every prerendered page that transitively touched this
// file (/dashboard/design-library, /dashboard/workflow,
// /dashboard/system-monitoring), with an opaque `undefined.trim()`.
//
// The Proxy keeps the public API byte-identical for all 149 importers —
// `supabase.from(...)`, `supabase.auth`, `supabase.rpc(...)` all behave as
// before — but construction is deferred to first property access. Build-time
// prerender never touches a property, so it never constructs a client.
// Misconfiguration still fails fast, at the point of real use, and now names
// the missing variable.

import { createClient } from "@/lib/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";

let cached: SupabaseClient | null = null;

/** One client per module instance, exactly as the eager version had. */
function getClient(): SupabaseClient {
  if (!cached) cached = createClient();
  return cached;
}

export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    const client = getClient() as unknown as Record<string | symbol, unknown>;
    const value = client[prop];
    // Bind methods so `this` stays the real client, not the Proxy — Supabase
    // relies on private fields internally, which break under a Proxy receiver.
    return typeof value === "function" ? value.bind(client) : value;
  },
  has(_target, prop) {
    return prop in (getClient() as unknown as object);
  },
  set(_target, prop, value) {
    (getClient() as unknown as Record<string | symbol, unknown>)[prop] = value;
    return true;
  },
});
